import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { map, Observable, tap } from 'rxjs';
import { environment } from '../../../../../../environments/environment';

export interface SupportedElementType {
  id: string;
  label: string;
  description?: string;
}

export interface ControlMethodDefinition {
  id: string;
  nameKey: string;
  usesBatching: boolean;
  defaultRules: Record<string, any>;
}

export interface ConcreteCertificationLevel {
  id: string;
  nameKey: string;
  description?: string;
}

export interface ControlStandard {
  _id: string;
  code: string;
  name: string;
  country?: string;
  acceptanceStrategy: string;
  
  // OLD SCHEMA (keep for backward compatibility)
  defaultRules?: any;
  rules?: any;
  
  // NEW ENHANCED SCHEMA
  controlMethods?: ControlMethodDefinition[];
  usesConcreteCertification?: boolean;
  concreteCertificationLevels?: ConcreteCertificationLevel[];
  supportedElementTypes?: SupportedElementType[];
}

@Injectable({
  providedIn: 'root'
})
export class StandardsService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.API_URL}/standards`;
  private readonly legacyApiUrl = `${environment.API_URL}/quality-control`;

  // Signals for reactive state
  readonly availableStandards = signal<ControlStandard[]>([]);
  readonly loadingStandards = signal(false);

  find(query = ''): Observable<HttpResponse<any>> {
    return this.http.get<any>(`${this.apiUrl}${query}`, { observe: 'response' }).pipe(
      map(response => response)
    );
  }

  /**
   * Create a new standard (Superadmin only)
   */
  create(standardData: Partial<ControlStandard>): Observable<ControlStandard> {
    return this.http.post<ControlStandard>(this.apiUrl, standardData);
  }

  /**
   * Get all standards with optional filters
   */
  findAll(params?: {
    code?: string;
    name?: string;
    country?: string;
    acceptanceStrategy?: string;
    usesConcreteCertification?: boolean;
    limit?: number;
    skip?: number;
    sort?: string;
    select?: string;
  }): Observable<ControlStandard[]> {
    this.loadingStandards.set(true);
    
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        const value = (params as any)[key];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }

    return this.http.get<ControlStandard[]>(this.apiUrl, { params: httpParams }).pipe(
      tap(standards => {
        this.availableStandards.set(standards);
        this.loadingStandards.set(false);
      })
    );
  }

  /**
   * Returns the total count of standards matching the given filter params.
   * Calls the same endpoint as findAll with `count=true` appended.
   */
  count(params?: {
    code?: string;
    name?: string;
    country?: string;
    acceptanceStrategy?: string;
    usesConcreteCertification?: boolean;
  }): Observable<number> {
    let httpParams = new HttpParams().set('count', 'true');
    if (params) {
      Object.keys(params).forEach(key => {
        const value = (params as any)[key];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }
    return this.http.get<number>(this.apiUrl, { params: httpParams });
  }

  /**
   * Get a specific standard by ID
   */
  findById(standardId: string, params?: {
    select?: string;
  }): Observable<ControlStandard> {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach(key => {
        const value = (params as any)[key];
        if (value !== undefined && value !== null) {
          httpParams = httpParams.set(key, value.toString());
        }
      });
    }

    return this.http.get<ControlStandard>(`${this.apiUrl}/${standardId}`, { params: httpParams });
  }

  /**
   * Get a standard by code
   */
  findByCode(code: string): Observable<ControlStandard> {
    return this.http.get<ControlStandard>(`${this.apiUrl}/code/${code}`);
  }

  /**
   * Update a standard (Superadmin only)
   */
  update(standardId: string, updateData: Partial<ControlStandard>): Observable<ControlStandard> {
    return this.http.patch<ControlStandard>(`${this.apiUrl}/${standardId}`, updateData);
  }

  /**
   * Delete a standard (Superadmin only)
   */
  delete(standardId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${standardId}`);
  }

  /**
   * Configure project with a standard (updated endpoint)
   */
  configureProject(projectId: string, standardId: string, overrides?: any, elementSets?: any[]): Observable<any> {
    return this.http.patch(`${this.apiUrl}/configure/${projectId}`, { standardId, overrides, elementSets });
  }

  // Legacy methods

  /**
   * Get available standards (legacy endpoint)
   */
  // getAvailableStandards(): redirect to findAll without query
}
